import { Section } from '@/components/layout/Section';
import { PageHero } from '@/components/brand/PageHero';
import { Watermark } from '@/components/brand/Watermark';
import { SplitText } from '@/components/motion/SplitText';
import { Reveal } from '@/components/motion/Reveal';
import { StaggerGroup } from '@/components/motion/StaggerGroup';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/ui/Logo';
import { TeamCarousel } from '@/components/ui/TeamCarousel';
import { about } from '@/content/about';
import { leadership } from '@/content/leadership';
import { site } from '@/content/site';
import { usePageMeta } from '@/lib/usePageMeta';

export default function About() {
  usePageMeta(
    'About Our Calgary Software Team',
    'Falcon Solutions is a custom software development company in Calgary, Alberta, with a delivery team in Lahore. You talk directly to the engineers building your software, not an account manager.',
    'About',
  );

  return (
    <>
      <PageHero sculpture label="About Falcon Solutions" headline="Closer to the team. Better software."
        description={about.hero.subcopy} action="Meet the people" target="#leadership"
        note="Calgary, Canada · Lahore, Pakistan" />
      <nav aria-label="About sections" className="page-jump-nav"><div className="shell flex flex-wrap gap-x-8 gap-y-1"><a href="#leadership">Our people</a><a href="#story">Our story</a><a href="#values">Our commitments</a><a href="#locations">Two connected teams</a></div></nav>

      {/* Leadership */}
      <Section id="leadership">
        <Reveal>
          <p className="text-eyebrow opacity-65">Our team</p>
          <h2 className="text-display-md mt-4 max-w-[16ch]">Who you will be working with</h2>
        </Reveal>

        <Reveal delay={0.1} className="mt-14">
          <TeamCarousel people={leadership} />
        </Reveal>
      </Section>

      {/* Story */}
      <Section id="story" tone="blue">
        <div className="grid gap-12 lg:grid-cols-12">
          {/*
            5 columns, not 4: at 4 the word "COMMUNICATION" was wider than the
            column and overflowed into the body text. `hyphens-auto` and a
            min-w-0 track stop any single long word doing that again.
          */}
          <div className="min-w-0 lg:col-span-5">
            <Reveal>
              <p className="text-eyebrow opacity-65">{about.story.eyebrow}</p>
              <h2 className="text-display-md mt-4 hyphens-auto">{about.story.heading}</h2>
            </Reveal>
          </div>
          <div className="min-w-0 lg:col-span-7">
            {about.story.body.map((para, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className="text-body-lg mb-6 opacity-75">{para}</p>
              </Reveal>
            ))}
            <Reveal delay={0.24}>
              <blockquote className="mt-10 border-l-2 border-brand pl-6">
                <p
                  className="font-display text-2xl leading-snug font-bold md:text-3xl"
                  style={{ fontStretch: '100%' }}
                >
                  {about.story.pullQuote}
                </p>
              </blockquote>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Values */}
      <Section id="values">
        <Reveal>
          <p className="text-eyebrow opacity-65">{about.values.eyebrow}</p>
          <h2 className="text-display-md mt-4 max-w-[16ch]">{about.values.heading}</h2>
        </Reveal>

        <StaggerGroup className="mt-14 grid gap-6 sm:grid-cols-2">
          {about.values.items.map((item) => (
            <div key={item.title} className="hover-card rounded-card border border-hairline bg-white p-8 shadow-card">
              <h3
                className="font-display text-xl font-bold"
                style={{ fontStretch: '100%' }}
              >
                {item.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed opacity-70">{item.body}</p>
            </div>
          ))}
        </StaggerGroup>
      </Section>

      {/* The falcon metaphor */}
      <Section tone="dark" pattern>
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-eyebrow text-white/65">{about.metaphor.eyebrow}</p>
              <h2 className="text-display-md mt-4 max-w-[14ch]">
                {about.metaphor.heading}
              </h2>
              <p className="text-body-lg mt-6 max-w-[54ch] text-white/65">{about.metaphor.body}</p>
            </Reveal>
          </div>
          <div className="flex justify-center lg:col-span-5">
            <Reveal delay={0.15}>
              <Logo variant="mark" size={200} className="text-brand-accent" title="" />
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Locations */}
      <Section id="locations" tone="muted">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-eyebrow opacity-65">{about.locations.eyebrow}</p>
              <h2 className="text-display-md mt-4">{about.locations.heading}</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <p className="text-body-lg opacity-75">{about.locations.body}</p>
            </Reveal>
            <StaggerGroup className="office-connection mt-10 grid gap-6 sm:grid-cols-2">
              {site.locations.map((loc) => (
                <div key={loc.city} className="office-card relative rounded-card border border-hairline bg-white p-7">
                  <h3
                    className="font-display text-2xl font-bold"
                    style={{ fontStretch: '100%' }}
                  >
                    {loc.city}
                  </h3>
                  <p className="mt-2 text-sm opacity-60">
                    {loc.region}, {loc.country}
                  </p>
                  <p className="mt-4 text-xs tracking-widest opacity-65">{loc.timezone}</p>
                </div>
              ))}
            </StaggerGroup>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section tone="gradient">
        <Watermark position="right" scale={1.5} opacity={0.05} />
        <div className="relative z-10 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <SplitText
              as="h2"
              text={about.cta.heading}
              className="text-display-md max-w-[14ch] leading-[1.08]"
            />
            <Reveal delay={0.25}>
              <p className="text-body-lg mt-5 max-w-[48ch] text-white/70">{about.cta.body}</p>
            </Reveal>
          </div>
          <Reveal delay={0.4} className="shrink-0">
            <Button href={about.cta.button.href} variant="inverse" size="lg">
              {about.cta.button.label}
            </Button>
          </Reveal>
        </div>
      </Section>
    </>
  );
}
